import { Location } from "@angular/common";
import { inject, Injectable, signal, Signal } from "@angular/core";

import { CookieService } from "ngx-cookie-service";

import { Context, LOCALE, StorageService } from "@hermes/core-app-core";

import {
    AB_TEST_LOCAL_STORAGE_KEY,
    AB_TEST_LOCAL_STORAGE_SEPARATOR,
    PREFIX_A_B_TEST_KEY,
} from "./ab-testing.constant";
import {
    getABTestLocaleCode,
    isCurrentLocaleABTestCookie,
} from "./ab-testing.helper";
import { ABTestingVariantValue } from "./ab-testing.type";

@Injectable()
export class ABTestingService {
    readonly #cookieService = inject(CookieService);
    readonly #locale = inject(LOCALE);
    readonly #context = inject(Context);
    readonly #location = inject(Location);
    readonly #storageService = inject(StorageService);

    /**
     * Retrieve the affected variant in the current context.
     * @param name The name of the A/B Test, for instance `homepage-2026-09`
     * @param callbackDefaultVariant Optional fallback callback invoked when no active variant is found
     * @returns the affected variant if existing, the fallback variant if provided, or `undefined` when no variant is available
     */
    public getActiveVariant(
        name: string,
        callbackDefaultVariant?: () => Signal<ABTestingVariantValue>,
    ): Signal<ABTestingVariantValue | undefined> {
        const currentVariant = this.#context.isInBrowserMode()
            ? this.getVariantFromCookies(name)
            : this.getVariantFromQueryParams(name);

        if (currentVariant) {
            return signal(currentVariant);
        }

        return callbackDefaultVariant
            ? callbackDefaultVariant()
            : signal(undefined);
    }

    /**
     * Synchronizes A/B Test cookies for current locale on local storage.
     * It will be used to data management.
     */
    public syncABTestToLocalStorage(): void {
        if (this.#context.isInServerMode()) {
            return;
        }

        /* Retrieve on cookies all A/B Testing for current locale */
        const localeCode = getABTestLocaleCode(this.#locale);
        const abTests: string[] = Object.entries(this.#cookieService.getAll())
            .filter(([cookieName]) =>
                isCurrentLocaleABTestCookie(cookieName, localeCode),
            )
            .map(
                ([cookieName, value]) =>
                    // Remove prefix and concatenate variant
                    `${cookieName.slice(PREFIX_A_B_TEST_KEY.length)}${AB_TEST_LOCAL_STORAGE_SEPARATOR}${value}`,
            );

        if (abTests.length > 0) {
            this.#storageService.setLocalStorageItem(
                AB_TEST_LOCAL_STORAGE_KEY,
                abTests.join("|"),
            );
        } else {
            this.#storageService.removeLocalStorageItem(
                AB_TEST_LOCAL_STORAGE_KEY,
            );
        }
    }

    private getVariantFromCookies(name: string): string | undefined {
        return this.#cookieService.get(this.getCookieName(name));
    }

    private getVariantFromQueryParams(name: string): string | null {
        const searchParameters: URLSearchParams = new URL(
            this.#location.path(false),
            "https://dummy",
        ).searchParams;

        return searchParameters.get(this.getQueryParamName(name));
    }

    private getCookieName(
        name: string,
    ): `${typeof PREFIX_A_B_TEST_KEY}${string}_${string}` {
        return `${PREFIX_A_B_TEST_KEY}${getABTestLocaleCode(this.#locale)}_${name}`;
    }

    private getQueryParamName(
        name: string,
    ): `${typeof PREFIX_A_B_TEST_KEY}${string}` {
        return `${PREFIX_A_B_TEST_KEY}${name}`;
    }
}
